import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { useAuth } from '../hooks/useAuth';
import { useDashboardStats } from '../hooks/useDashboardStats';
// TEMPORARILY COMMENTED OUT - Notification system disabled
// import { useNotifications } from '../hooks/useNotifications';
import { ROUTES } from '../constants';
import Card, { CardHeader, CardBody, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge, { StatusBadge } from '../components/ui/Badge';
import EmptyState from '../components/ui/EmptyState';
import Icon from '../components/ui/Icon';
import { observePreload, attachHoverPreload } from '../utils/preload';
import './Dashboard.css';

const loadPurchaseOrderCreate = () => import('./PurchaseOrders/POCreatePage');
const loadInventoryCreate = () => import('./Inventory/InventoryCreatePage');
const loadSupplierCreate = () => import('./Suppliers/SupplierCreatePage');

const Dashboard = () => {
  const { user, accessibleFeatures, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { stats, recentPOs, isLoading, categoryStates, error } = useDashboardStats();

  // TEMPORARILY COMMENTED OUT - Notification system disabled
  // const { notifications, unreadCount } = useNotifications();
  const unreadCount = 0;

  const purchaseOrderShortcutRef = useRef(null);
  const inventoryShortcutRef = useRef(null);
  const supplierShortcutRef = useRef(null);

  // Check permissions for quick actions
  const canCreatePurchaseOrder = hasPermission('purchaseOrderCreateAccess');
  const canCreateInventory = hasPermission('inventoryManagementAccess');
  const canCreateSupplier = hasPermission('supplierCreateAccess');

  useEffect(() => {
    const disposers = [];
    if (purchaseOrderShortcutRef.current && canCreatePurchaseOrder) {
      disposers.push(observePreload(purchaseOrderShortcutRef.current, loadPurchaseOrderCreate));
      disposers.push(attachHoverPreload(purchaseOrderShortcutRef.current, loadPurchaseOrderCreate));
    }
    if (inventoryShortcutRef.current && canCreateInventory) {
      disposers.push(observePreload(inventoryShortcutRef.current, loadInventoryCreate));
      disposers.push(attachHoverPreload(inventoryShortcutRef.current, loadInventoryCreate));
    }
    if (supplierShortcutRef.current && canCreateSupplier) {
      disposers.push(observePreload(supplierShortcutRef.current, loadSupplierCreate));
      disposers.push(attachHoverPreload(supplierShortcutRef.current, loadSupplierCreate));
    }
    return () => {
      disposers.forEach((dispose) => {
        if (typeof dispose === 'function') {
          dispose();
        }
      });
    };
  }, [canCreatePurchaseOrder, canCreateInventory, canCreateSupplier]);

  const handleKPIClick = (type) => {
    switch (type) {
      case 'totalProducts':
        navigate(ROUTES.INVENTORY);
        break;
      case 'lowStockItems':
        navigate(`${ROUTES.INVENTORY}?status=low_stock`);
        break;

      case 'pendingPOs':
        // Pending includes both DRAFT and APPLIED statuses
        // Use 'pending' as a special filter value that frontend will handle
        navigate(`${ROUTES.PURCHASE_ORDERS}?status=pending`);
        break;
      case 'activeMachines':
        navigate(ROUTES.MACHINES);
        break;
      default:
        break;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Layout>
      <div className="dashboard">
        {/* Welcome Section */}
        <div className="dashboard-welcome">
          <div className="dashboard-welcome-content">
            <h1>Welcome back, {user?.name || user?.email || 'User'}!</h1>
            <p>Here's what's happening with your inventory today.</p>
          </div>
          {unreadCount > 0 && (
            <div className="dashboard-notification-alert">
              <Badge variant="warning" size="small">
                {unreadCount} new notifications
              </Badge>
            </div>
          )}
        </div>

        {/* Global Error (Only for unexpected global errors) */}
        {error && (
          <div className="dashboard-error">
            <Badge variant="danger">Error loading dashboard data: {error}</Badge>
          </div>
        )}

        {/* Categories specific errors (Only non-permission errors) */}
        <div className="dashboard-category-errors">
          {categoryStates.inventory.error && (
            <div className="dashboard-error-summary">
              <Badge variant="danger">Inventory: {categoryStates.inventory.error}</Badge>
            </div>
          )}
          {categoryStates.machine.error && (
            <div className="dashboard-error-summary">
              <Badge variant="danger">Machines: {categoryStates.machine.error}</Badge>
            </div>
          )}
          {categoryStates.purchaseOrder.error && (
            <div className="dashboard-error-summary">
              <Badge variant="danger">Orders: {categoryStates.purchaseOrder.error}</Badge>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        <div className="dashboard-stats">
          {categoryStates.inventory.hasAccess && (
            <Card
              className="stat-card stat-card-clickable"
              clickable={true}
              onClick={() => handleKPIClick('totalProducts')}
              tabIndex={0}
              role="button"
              aria-label="View all inventory items"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleKPIClick('totalProducts');
                }
              }}
            >
              <CardBody>
                <div className="stat-content">
                  <div className="stat-icon">
                    <Icon name="inventory" size="large" />
                  </div>
                  <div className="stat-info">
                    <h3>Total Products</h3>
                    <p className="stat-number">
                      {categoryStates.inventory.loading ? '...' : stats.totalProducts.toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {categoryStates.machine.hasAccess && (
            <Card
              className="stat-card stat-card-clickable"
              clickable={true}
              onClick={() => handleKPIClick('activeMachines')}
              tabIndex={0}
              role="button"
              aria-label="View active machines"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleKPIClick('activeMachines');
                }
              }}
            >
              <CardBody>
                <div className="stat-content">
                  <div className="stat-icon">
                    <Icon name="precision_manufacturing" size="large" />
                  </div>
                  <div className="stat-info">
                    <h3>Active Machines</h3>
                    <p className="stat-number">
                      {categoryStates.machine.loading ? '...' : stats.activeMachines.toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {categoryStates.inventory.hasAccess && (
            <Card
              className="stat-card stat-card-clickable"
              clickable={true}
              onClick={() => handleKPIClick('lowStockItems')}
              tabIndex={0}
              role="button"
              aria-label="View low stock items"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleKPIClick('lowStockItems');
                }
              }}
            >
              <CardBody>
                <div className="stat-content">
                  <div className="stat-icon">
                    <Icon name="warning" size="large" />
                  </div>
                  <div className="stat-info">
                    <h3>Low Stock Items</h3>
                    <p className="stat-number">
                      {categoryStates.inventory.loading ? '...' : stats.lowStockItems}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {categoryStates.purchaseOrder.hasAccess && (
            <Card
              className="stat-card stat-card-clickable"
              clickable={true}
              onClick={() => handleKPIClick('pendingPOs')}
              tabIndex={0}
              role="button"
              aria-label="View pending purchase orders"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleKPIClick('pendingPOs');
                }
              }}
            >
              <CardBody>
                <div className="stat-content">
                  <div className="stat-icon">
                    <Icon name="receipt_long" size="large" />
                  </div>
                  <div className="stat-info">
                    <h3>Pending POs</h3>
                    <p className="stat-number">
                      {categoryStates.purchaseOrder.loading ? '...' : stats.pendingPOs}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </div>

        {/* Quick Actions */}
        <div className="dashboard-section">
          {(canCreatePurchaseOrder || canCreateInventory || canCreateSupplier) && (
            <>
              <h2>Quick Actions</h2>
              <div className="dashboard-actions">
                {/* New Purchase Order */}
                {canCreatePurchaseOrder && (
                  <Link
                    to={ROUTES.PO_CREATE}
                    ref={purchaseOrderShortcutRef}
                  >
                    <Button variant="primary" size="large" className="action-button">
                      <Icon name="add" size="medium" className="action-icon" />
                      <span>New Purchase Order</span>
                    </Button>
                  </Link>
                )}

                {/* Add Inventory Item */}
                {canCreateInventory && (
                  <Link
                    to={ROUTES.INVENTORY_CREATE}
                    ref={inventoryShortcutRef}
                  >
                    <Button variant="outline" size="large" className="action-button">
                      <Icon name="inventory" size="medium" className="action-icon" />
                      <span>Add Inventory Item</span>
                    </Button>
                  </Link>
                )}

                {/* Add Supplier */}
                {canCreateSupplier && (
                  <Link
                    to={ROUTES.SUPPLIER_CREATE}
                    ref={supplierShortcutRef}
                  >
                    <Button variant="outline" size="large" className="action-button">
                      <Icon name="business" size="medium" className="action-icon" />
                      <span>Add Supplier</span>
                    </Button>
                  </Link>
                )}
              </div>
            </>
          )}
        </div>

        {/* Main Content Grid */}
        <div className="dashboard-content">
          {/* Recent Purchase Orders */}
          {categoryStates.purchaseOrder.hasAccess && (
            <div className="dashboard-widget">
              <Card>
                <CardHeader>
                  <CardTitle>Recent Purchase Orders</CardTitle>
                  <Link to={ROUTES.PURCHASE_ORDERS}>
                    <Button variant="outline" size="small">
                      View All
                    </Button>
                  </Link>
                </CardHeader>
                <CardBody>
                  {categoryStates.purchaseOrder.loading ? (
                    <div className="dashboard-loading">Loading recent purchase orders...</div>
                  ) : recentPOs.length === 0 ? (
                    <EmptyState
                      icon="receipt_long"
                      title="No recent purchase orders"
                      message="Create your first purchase order to get started."
                      action={
                        canCreatePurchaseOrder ? (
                          <Link to={ROUTES.PO_CREATE}>
                            <Button variant="primary">Create PO</Button>
                          </Link>
                        ) : null
                      }
                    />
                  ) : (
                    <div className="po-list">
                      {recentPOs.map((po) => {
                        const poId = po.id || po.poId;
                        const poNumber = po.poNumber || po.po_number || poId;
                        const supplier = po.supplier || po.supplierName || `Supplier ${(po.supplierId || '').slice(-4)}`;
                        const total = po.total || po.totalAmount || 0;
                        const createdBy = po.createdBy || `User ${(po.created_by || '').slice(-4)}`;
                        const createdAt = po.createdAt || po.created_at;
                        const status = po.status || 'DRAFT';

                        return (
                          <div key={poId} className="po-item">
                            <div className="po-info">
                              <div className="po-header">
                                <Link to={`${ROUTES.PO_DETAIL.replace(':id', poId)}`} className="po-number">
                                  {poNumber}
                                </Link>
                                <StatusBadge status={status} size="small" />
                              </div>
                              <div className="po-details">
                                <span className="po-supplier">{supplier}</span>
                                <span className="po-total">{formatCurrency(total)}</span>
                              </div>
                              <div className="po-meta">
                                <span>By {createdBy}</span>
                                {createdAt && <span>{formatDate(createdAt)}</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
